import { Box, Button, Card, CardContent, CardHeader, Container, Grid, InputLabel, MenuItem, TextField, Typography } from "@material-ui/core";
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Form, Formik } from "formik";
import MaterialTable from 'material-table';
import { useEffect, useState } from "react";
import { useAlert } from 'react-alert';
import { trackPromise } from 'react-promise-tracker';
import * as Yup from "yup";
import Services from "../Services";
import React from "react";

const formatNumberWithThousandSeparators = (input) => {
  let value = String(input).replace(/[^0-9.]/g, '');
  const parts = value.split('.');
  if (parts.length > 1) {
    parts[1] = parts[1].slice(0, 2);
    value = parts[0] + (parts[1] ? `.${parts[1]}` : '');
  }
  const formattedInteger = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (formattedInteger + (parts.length > 1 ? `.${parts[1]}` : ''));
};

export function AccountEntries({ factoryID, isCredit, legerAccount, setTransactions, setVarianceBalance, netAmount, tableLength, vatAmount, vatAccount }) {
  const [childHeaders, setChildHeaders] = useState([]);
  const [creditledgerAccounts, setCreditLedgerAccounts] = useState([]);
  const [debitledgerAccounts, setDebitLedgerAccounts] = useState([]);
  const [debitAdded, setDebitAdded] = useState(false);
  const [accountEntries, setAccountEntries] = useState({
    creditChildHeader: "",
    creditLedger: 0,
    debitChildHeader: "",
    debitLedger: 0,
    description: "",
    debit: '',
    credit: '',
    ledgerAccountID: 0
  });

  const [isExpanded, setIsExpanded] = useState(true);
  const [tableData, setTableData] = useState([]);
  const [totalCredit, setTotalCredit] = useState(0);
  const [variance, setVariance] = useState(0);

  const alert = useAlert();

  const toggleComponent = () => setIsExpanded(!isExpanded);

  useEffect(() => {
    trackPromise(
      getChildHeadersForAccountEntriesDropDown(),
    );
  }, [factoryID]);

  useEffect(() => {
    if (isCredit) {
      trackPromise(
        getChildHeadersForAccountEntriesIsCredit(),
      );
    }
  }, [isCredit]);

  useEffect(() => {
    if (legerAccount && (legerAccount.childHeaderID || legerAccount.ledgerAccountID)) {
      setAccountEntries((prev) => ({
        ...prev,
        debitChildHeader: legerAccount?.childHeaderID ?? prev.debitChildHeader,
        debitLedger: legerAccount?.ledgerAccountID ?? prev.debitLedger,
      }));
    }
  }, [legerAccount]);

  useEffect(() => {
    const totalCreditAmount = tableData.reduce((sum, entry) => sum + (entry.credit || 0), 0);
    const totalDebitAmount = tableData.reduce((sum, entry) => sum + (entry.debit || 0), 0);
    setTotalCredit(totalCreditAmount);
    setVariance(totalDebitAmount - totalCreditAmount);
  }, [tableData]);

  useEffect(() => {
    if (tableData.length > 0) {
      setTransactions(tableData);
    }
  }, [tableData]);

  useEffect(() => {
    setVarianceBalance(variance);
  }, [variance]);

  useEffect(() => {
    if (accountEntries.creditChildHeader != null && accountEntries.creditChildHeader > 0) {
      getCreditLedgerAccounts();
    }
  }, [accountEntries.creditChildHeader]);

  useEffect(() => {
    if (accountEntries.debitChildHeader != null && accountEntries.debitChildHeader > 0) {
      getDebitLedgerAccounts();
    }
  }, [accountEntries.debitChildHeader]);

  useEffect(() => {
    setAccountEntries((prevState) => ({
      ...prevState,
      debit: netAmount ? (netAmount - (vatAmount || 0)) : 0,
      credit: netAmount ? (netAmount - (vatAmount || 0)) : 0,
    }));
  }, [netAmount, vatAmount]);

  async function getChildHeadersForAccountEntriesDropDown() {
    const response = await Services.getChildHeadersForAccountEntriesDropDown(factoryID);
    setChildHeaders(response.map(header => ({ ...header, id: Number(header.id) })));
  }

  async function getChildHeadersForAccountEntriesIsCredit() {
    const response = await Services.GetOtherPaybleAccountForGRN(factoryID);
    setAccountEntries((prevState) => ({
      ...prevState,
      creditChildHeader: response[0]?.childHeaderID,
      credit: netAmount ? (netAmount - (vatAmount || 0)) : 0,
    }));
  }

  async function getCreditLedgerAccounts() {
    const response = await Services.getLedgerAccountsForAccountEntriesDropDownInventory(factoryID, accountEntries.creditChildHeader);
    const { debitAccounts, creditAccounts } = response.reduce((acc, record) => {
      acc.debitAccounts.push({
        id: record.ledgerAccountID,
        name: record.ledgerAccountName,
        parentTransactionModeID: record.parentTransactionModeID
      })
      acc.creditAccounts.push({
        id: record.ledgerAccountID,
        name: record.ledgerAccountName,
        parentTransactionModeID: record.parentTransactionModeID
      })
      return acc;
    }, { debitAccounts: [], creditAccounts: [] });
    setCreditLedgerAccounts(creditAccounts)
  }

  async function getDebitLedgerAccounts() {
    const response = await Services.getLedgerAccountsForAccountEntriesDropDownInventory(factoryID, accountEntries.debitChildHeader);
    const { debitAccounts, creditAccounts } = response.reduce((acc, record) => {
      acc.debitAccounts.push({
        id: record.ledgerAccountID,
        name: record.ledgerAccountName,
        parentTransactionModeID: record.parentTransactionModeID
      })
      acc.creditAccounts.push({
        id: record.ledgerAccountID,
        name: record.ledgerAccountName,
        parentTransactionModeID: record.parentTransactionModeID
      })
      return acc;
    }, { debitAccounts: [], creditAccounts: [] });
    setDebitLedgerAccounts(debitAccounts)
  }

  // async function getLedgerAccountsByChildHeaderId(childHeaderId) {
  //   const response = await commonService.getLedgerAccountsByChildHeaderId(childHeaderId);
  //   setLedgerAccounts(response);
  // }

  const generateDropDownMenu = (data) => {
    return data ? data.map(({ id, name }) => (
      <MenuItem key={id} value={id}>{name}</MenuItem>
    )) : [];
  };

  const validationSchema = Yup.object().shape({
    creditChildHeader: Yup.string().required("Credit child is required"),
    creditLedger: Yup.string().required("Credit ledger is required"),
    debitChildHeader: Yup.string().required("Debit child is required"),
    debitLedger: Yup.string().required("Debit ledger is required"),
    debit: Yup.number()
      .transform((value, originalValue) => {
        if (typeof originalValue === 'string') {
          const cleaned = originalValue.replace(/,/g, '');
          return parseFloat(cleaned);
        }
        return value;
      })
      .when('debitChildHeader', {
        is: (debitChildHeader) => !!debitChildHeader,
        then: Yup.number().required("Debit amount is required").positive("Amount must be positive"),
      }),
    credit: Yup.number()
      .transform((value, originalValue) => {
        if (typeof originalValue === 'string') {
          const cleaned = originalValue.replace(/,/g, '');
          return parseFloat(cleaned);
        }
        return value;
      })
      .when('creditChildHeader', {
        is: (creditChildHeader) => !!creditChildHeader,
        then: Yup.number().required("Credit amount is required").positive("Amount must be positive"),
      }),
  });

  const handleSubmit = (values) => {

  };

  const handleAddDebitEntry = (values, resetForm) => {
    if (values.debitLedger <= 0 || values.debitLedger === "" || values.debit <= 0 || values.debit === "") {
      alert.error("Please select a debit ledger and enter a positive amount.");
      return;
    }

    const debitChildHeaderName = childHeaders.find(
      (header) => String(header.id) == String(values.debitChildHeader)
    )?.name || "";

    const debitLedgerName = debitledgerAccounts.find(
      (ledger) => String(ledger.id) == String(values.debitLedger)
    )?.name || "";

    const newEntry = {
      childHeader: debitChildHeaderName,
      ledgerAccount: debitLedgerName,
      description: values.description || "",
      debit: parseFloat(values.debit.toString().replace(/,/g, '')) || 0,
      credit: 0,
      amount: values.debit || 0,
      ledgerAccountID: values.debitLedger || 0,
      parentTransactionModeID: debitledgerAccounts.find((ledger) => String(ledger.id) == String(values.debitLedger))?.parentTransactionModeID || 0
    };

    let entriesToAdd = [newEntry];

    // Auto-Add VAT Entry
    if (vatAmount > 0) {
      if (vatAccount && vatAccount.ledgerAccountID) {
        const vatEntry = {
          childHeader: "VAT",
          ledgerAccount: vatAccount.ledgerAccountName || "VAT Account",
          description: "VAT Amount",
          debit: vatAmount,
          credit: 0,
          amount: vatAmount,
          ledgerAccountID: vatAccount.ledgerAccountID,
          parentTransactionModeID: vatAccount.parentTransactionModeID || 0
        };
        entriesToAdd.push(vatEntry);
      } else {
        alert.error("Cannot find VAT account details.");
      }
    }

    setTableData([...tableData, ...entriesToAdd]);

    setAccountEntries((prev) => ({
      ...prev,
      debitChildHeader: values.debitChildHeader,
      debitLedger: values.debitLedger,
      description: "",
      debit: "",
      creditChildHeader: isCredit ? prev.creditChildHeader : "",
      creditLedger: isCredit ? prev.creditLedger : "",
      credit: !isCredit ? values.debit : (!isCredit ? "" : prev.credit),
    }));
  };

  const handleAddCreditEntry = (values, resetForm) => {
    if ((values.creditLedger <= 0 || values.creditLedger == "") && (values.credit < 0) || values.credit == "" || values.credit > netAmount) {
      alert.error("Please enter a valid credit ledger and amount.");
      return;
    }

    const creditChildHeaderName = childHeaders.find(
      (header) => String(header.id) == String(values.creditChildHeader)
    )?.name || "";

    const creditLedgerName = creditledgerAccounts.find(
      (ledger) => String(ledger.id) == String(values.creditLedger)
    )?.name || "";

    const enteredCredit = parseFloat(values.credit.toString().replace(/,/g, '')) || 0;
    const finalCredit = enteredCredit + (vatAmount || 0);

    const newEntry = {
      childHeader: creditChildHeaderName,
      ledgerAccount: creditLedgerName,
      description: values.description || "",
      debit: 0,
      credit: finalCredit,
      amount: -(finalCredit), // Assuming negative convention for credit in amount field, though typically amount field is just magnitude
      ledgerAccountID: values.creditLedger || 0,
      parentTransactionModeID: creditledgerAccounts.find((ledger) => String(ledger.id) == String(values.creditLedger))?.parentTransactionModeID || 0
    };

    setTableData([...tableData, newEntry]);
    resetForm();
    setAccountEntries((prev) => ({
      ...prev,
      creditChildHeader: isCredit ? prev.creditChildHeader : "",
      creditLedger: isCredit ? prev.creditLedger : "",
      // debitChildHeader: "",
      // debitLedger: "",
      // description: "",
      // debit: '',
      // credit: "",
    }));
  };

  const handleEditEntry = (index) => {
    const entryToEdit = tableData[index];
    const isDebit = entryToEdit.debit > 0;

    if (isDebit) {
      const debitChildHeaderId = childHeaders.find(
        (header) => header.name == entryToEdit.childHeader
      )?.id || "";

      const debitLedgerId = debitledgerAccounts.find(
        (ledger) => ledger.name === entryToEdit.ledgerAccount
      )?.id || "";

      setAccountEntries({
        ...accountEntries,
        debitChildHeader: debitChildHeaderId,
        debitLedger: debitLedgerId,
        description: entryToEdit.description,
        debit: entryToEdit.debit,
        credit: 0,
      });
    } else {
      const creditChildHeaderId = childHeaders.find(
        (header) => header.name == entryToEdit.childHeader
      )?.id || "";

      const creditLedgerId = creditledgerAccounts.find(
        (ledger) => ledger.name === entryToEdit.ledgerAccount
      )?.id || "";

      setAccountEntries({
        ...accountEntries,
        creditChildHeader: creditChildHeaderId,
        creditLedger: creditLedgerId,
        description: entryToEdit.description,
        credit: entryToEdit.credit,
        debit: 0,
      });
    }

    setTableData(tableData.filter((_, i) => i !== index));
  };

  const handleDeleteEntry = (index) => {
    const deletedEntry = tableData[index];

    const updatedTableData = tableData.filter((_, i) => i !== index);
    setTableData(updatedTableData);

    if (deletedEntry.debit > 0) {
      setAccountEntries(prev => ({
        ...prev,
        debitChildHeader: legerAccount?.childHeaderID || "",
        debitLedger: legerAccount?.ledgerAccountID || "",
        debit: netAmount ? (netAmount - (vatAmount || 0)) : 0,
      }));
    }
  };

  const columns = [
    { title: "Child Header", field: "childHeader" },
    { title: "Ledger Account", field: "ledgerAccount" },
    { title: "Description", field: "description" },
    { title: "Debit", field: "debit", type: "numeric", render: rowData => rowData.debit > 0 ? formatNumberWithThousandSeparators(rowData.debit) : "" },
    { title: "Credit", field: "credit", type: "numeric", render: rowData => rowData.credit > 0 ? formatNumberWithThousandSeparators(rowData.credit) : "" },
    {
      title: "Action",
      field: "actions",
      render: (rowData) => (
        <>
          {/* <Button
            color="primary"
            size="small"
            onClick={() => handleEditEntry(rowData.tableData.id)}
          >
            <EditIcon />
          </Button> */}
          <Button
            color="secondary"
            size="small"
            onClick={() => handleDeleteEntry(rowData.tableData.id)}
          >
            <DeleteIcon />
          </Button>
        </>
      ),
    },
  ];

  function cardTitle(titleName) {
    return (
      <Grid container spacing={1}>
        <Grid item md={10} xs={12}>
          {titleName}
        </Grid>
      </Grid>
    );
  }

  function handleChange(e) {
    const { name, value } = e.target;
    let formattedValue = value;

    setAccountEntries((prevState) => ({
      ...prevState,
      [name]: formattedValue || '',
    }));
  }

  function handleSearchDropdownChangeDebit(data, e) {
    if (data === undefined || data === null) {
      setAccountEntries({
        ...accountEntries,
        debitChildHeader: '0'
      });
      return;
    } else {
      var nameV = "name";
      var valueV = data["id"];

      setAccountEntries({
        ...accountEntries,
        // Keep numeric to avoid type mismatches later
        debitChildHeader: Number(valueV)
      });
    }

  }

  function handleSearchDropdownChangeCredit(data, e) {
    if (data === undefined || data === null) {
      setAccountEntries({
        ...accountEntries,
        creditChildHeader: '0'
      });
      return;
    } else {
      var nameV = "name";
      var valueV = data["id"];

      setAccountEntries({
        ...accountEntries,
        // Keep numeric to avoid type mismatches later
        creditChildHeader: Number(valueV)
      });
    }

  }

  return (
    <Formik
      initialValues={accountEntries}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values, errors, touched, handleBlur, setFieldValue, resetForm }) => (
        <Form>
          <Container maxWidth="lg">
            <Box p={3}>
              <Card>
                <CardHeader
                  title={cardTitle("Account Entries")}
                  action={
                    <Button size="small" onClick={toggleComponent}>
                      {isExpanded ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />}
                    </Button>
                  }
                />
                {isExpanded && (
                  <Grid container spacing={2} justifyContent="center" alignItems="center" style={{ padding: "20px" }}>
                    <Grid item xs={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box mb={2}>
                            <Typography variant="h6">Debit</Typography>
                          </Box>
                          <Box mb={2}>
                            <InputLabel shrink id="debitChildHeader">
                              Child Header
                            </InputLabel>
                            {values.debitChildHeader === 0 ?
                              <Autocomplete
                                options={childHeaders}
                                disabled={true}
                                getOptionLabel={(option) => option.name || ""}
                                value={childHeaders.find((header) => header.id === values.debitChildHeader) || null}
                                onChange={(e, value) => handleSearchDropdownChangeDebit(value, e)}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    error={touched.debitChildHeader && Boolean(errors.debitChildHeader)}
                                    helperText={touched.debitChildHeader && errors.debitChildHeader}
                                    variant="outlined"
                                    size="small"
                                    placeholder="Select Child Header"
                                    disabled={values.credit > 0}
                                  />
                                )}
                              />
                              :
                              <TextField
                                select
                                name="debitChildHeader"
                                value={values.debitChildHeader}
                                onChange={(e) => handleChange(e)}
                                onBlur={handleBlur}
                                error={touched.debitChildHeader && Boolean(errors.debitChildHeader)}
                                helperText={touched.debitChildHeader && errors.debitChildHeader}
                                variant="outlined"
                                fullWidth
                                size="small"
                                disabled={true}
                              >
                                <MenuItem value="">--Select Child Header--</MenuItem>
                                {generateDropDownMenu(childHeaders)}
                              </TextField>
                            }
                          </Box>
                          <Box mb={2}>
                            <InputLabel shrink id="debitLedger">
                              Ledger Account
                            </InputLabel>
                            <TextField
                              select
                              name="debitLedger"
                              value={values.debitLedger}
                              onChange={(e) => handleChange(e)}
                              onBlur={handleBlur}
                              error={touched.debitLedger && Boolean(errors.debitLedger)}
                              helperText={touched.debitLedger && errors.debitLedger}
                              variant="outlined"
                              fullWidth
                              size="small"
                              disabled={true}
                            >
                              <MenuItem value="">--Select Ledger Account--</MenuItem>
                              {generateDropDownMenu(debitledgerAccounts)}
                            </TextField>
                          </Box>
                          <Box mb={2}>
                            <InputLabel shrink id="debit">
                              Amount
                            </InputLabel>
                            <TextField
                              name="debit"
                              value={formatNumberWithThousandSeparators(values.debit) || ""}
                              onChange={(e) => {
                                let value = e.target.value.replace(/[^0-9.]/g, '');
                                const parts = value.split('.');
                                if (parts.length > 1) {
                                  parts[1] = parts[1].slice(0, 2);
                                  e.target.value = parts.length > 1 ? `${parts[0]}.${parts[1]}` : parts[0];
                                }
                                handleChange(e);
                                setFieldValue('deductionAmount', e.target.value);
                              }}
                              onKeyDown={(e) => {
                                const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
                                if (e.key === '.' && e.target.value.includes('.')) {
                                  e.preventDefault();
                                }
                                if (!/^\d$/.test(e.key) && !allowedKeys.includes(e.key) && e.key !== '.') {
                                  e.preventDefault();
                                }
                              }}
                              onBlur={handleBlur}
                              error={touched.debit && Boolean(errors.debit)}
                              helperText={touched.debit && errors.debit}
                              variant="outlined"
                              fullWidth
                              size="small"
                              placeholder="Enter Amount"
                              disabled={true}
                            />
                          </Box>
                          <Box>
                            <InputLabel shrink id="description">
                              Description
                            </InputLabel>
                            <TextField
                              name="description"
                              value={values.description}
                              onChange={(e) => {
                                if (values.credit > 0) {
                                  setFieldValue("description", values.description);
                                } else {
                                  handleChange(e);
                                }
                              }}
                              variant="outlined"
                              fullWidth
                              size="small"
                              placeholder="Enter Description"
                              disabled
                            />
                          </Box>
                          <Box mt={2} style={{ textAlign: "right" }}>
                            <Button
                              color="primary"
                              variant="contained"
                              size="small"
                              onClick={() => handleAddDebitEntry(values, resetForm)}
                              disabled={tableData.some(x => x.debit === netAmount)}
                            >
                              Add Debit
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box mb={2}>
                            <Typography variant="h6">Credit</Typography>
                          </Box>
                          <Box mb={2}>
                            <InputLabel shrink id="creditChildHeader">
                              Child Header
                            </InputLabel>
                            {isCredit == false ?
                              <Autocomplete
                                options={childHeaders}
                                getOptionLabel={(option) => option.name || ""}
                                value={childHeaders.find((header) => header.id === values.creditChildHeader) || null}
                                onChange={(e, value) => handleSearchDropdownChangeCredit(value, e)}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    error={touched.creditChildHeader && Boolean(errors.creditChildHeader)}
                                    helperText={touched.creditChildHeader && errors.creditChildHeader}
                                    variant="outlined"
                                    size="small"
                                    placeholder="Select Child Header"
                                    disabled={values.debit > 0}
                                  />
                                )}
                              />
                              :
                              <TextField
                                select
                                name="creditChildHeader"
                                value={values.creditChildHeader}
                                onChange={(e) => handleChange(e)}
                                onBlur={handleBlur}
                                error={touched.creditChildHeader && Boolean(errors.creditChildHeader)}
                                helperText={touched.creditChildHeader && errors.creditChildHeader}
                                variant="outlined"
                                fullWidth
                                size="small"
                                disabled={true}
                              >
                                <MenuItem value="">--Select Child Header--</MenuItem>
                                {generateDropDownMenu(childHeaders)}
                              </TextField>
                            }
                          </Box>
                          <Box mb={2}>
                            <InputLabel shrink id="creditLedger">
                              Ledger Account
                            </InputLabel>
                            <TextField
                              select
                              name="creditLedger"
                              value={values.creditLedger}
                              onChange={(e) => handleChange(e)}
                              onBlur={handleBlur}
                              error={touched.creditLedger && Boolean(errors.creditLedger)}
                              helperText={touched.creditLedger && errors.creditLedger}
                              variant="outlined"
                              fullWidth
                              size="small"
                              disabled={values.creditChildHeader === ""}
                            >
                              <MenuItem value="">--Select Ledger Account--</MenuItem>
                              {generateDropDownMenu(creditledgerAccounts)}
                            </TextField>
                          </Box>
                          <Box mb={2}>
                            <InputLabel shrink id="credit">
                              Amount
                            </InputLabel>
                            <TextField
                              name="credit"
                              value={formatNumberWithThousandSeparators(values.credit) || ""}
                              onChange={(e) => {
                                let value = e.target.value.replace(/[^0-9.]/g, '');
                                const parts = value.split('.');
                                if (parts.length > 1) {
                                  parts[1] = parts[1].slice(0, 2);
                                  e.target.value = parts.length > 1 ? `${parts[0]}.${parts[1]}` : parts[0];
                                }
                                handleChange(e);
                                setFieldValue('deductionAmount', e.target.value);
                              }}
                              onKeyDown={(e) => {
                                const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
                                if (e.key === '.' && e.target.value.includes('.')) {
                                  e.preventDefault();
                                }
                                if (!/^\d$/.test(e.key) && !allowedKeys.includes(e.key) && e.key !== '.') {
                                  e.preventDefault();
                                }
                              }}
                              onBlur={handleBlur}
                              error={touched.credit && Boolean(errors.credit)}
                              helperText={touched.credit && errors.credit}
                              variant="outlined"
                              fullWidth
                              size="small"
                              placeholder="Enter Amount"
                              disabled={values.creditLedger === ""}
                            />
                          </Box>
                          <Box>
                            <InputLabel shrink id="description">
                              Description
                            </InputLabel>
                            <TextField
                              name="description"
                              value={values.description}
                              onChange={(e) => {
                                setFieldValue("description", values.description);
                                handleChange(e);
                              }}
                              variant="outlined"
                              fullWidth
                              size="small"
                              placeholder="Enter Description"
                            />
                          </Box>
                          <Box mt={2} style={{ textAlign: "right" }}>
                            <Button
                              color="primary"
                              variant="contained"
                              size="small"
                              onClick={() => handleAddCreditEntry(values, resetForm)}
                              disabled={!tableData.some(x => x.debit > 0) || variance === 0}
                            >
                              Add Credit
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                )}
                {isExpanded && (
                  <Box mt={3}>
                    <MaterialTable
                      title="Account Entries"
                      columns={columns}
                      data={tableData}
                      options={{
                        search: false,
                        paging: false,
                        actionsColumnIndex: -1,
                      }}
                    />
                    <Box mt={2} display="flex" justifyContent="space-between">
                      <Typography>Total Credit (Rs): {formatNumberWithThousandSeparators(totalCredit.toFixed(2))}</Typography>
                      <Typography>Variance (Rs): {formatNumberWithThousandSeparators(variance.toFixed(2))}</Typography>
                    </Box>
                    <Box mt={2} display="flex" justifyContent="space-between">
                      <Typography>Prepared By: Administrator ERP</Typography>
                    </Box>
                  </Box>
                )}
              </Card>
            </Box>
          </Container>
        </Form>
      )}
    </Formik>
  );
};

export default AccountEntries;
